import { EquipmentItem } from '@/backend/models/equipmentItem';
import { ParsedTask } from '@/backend/helpers/parseYamlTasks';

type ResolvableTask = Pick<ParsedTask, 'instrumentName' | 'taskKey' | 'hasAlias'>;

/**
 * Resolves the user-facing label for a commanding task.
 *
 * The raw YAML task key is `<equipment identifier>.<Method>` (e.g.
 * `dut-vdmm.GetDCVoltageMeasurement`). The internal identifier should never be
 * surfaced in the UI: when the label is exactly that raw key and its prefix matches
 * a known equipment item, swap the prefix for the equipment's human-readable `name`.
 *
 * Any other label — an explicit YAML `alias:`, or a prefix that matches an instrument
 * name / reserved word (veriform, resourcery) / not-yet-loaded equipment — is returned
 * unchanged.
 */
export function resolveTaskAlias(currentLabel: string, task: ResolvableTask | undefined, equipmentList: EquipmentItem[]): string {
  // Only rewrite when the label is the raw "<identifier>.<Method>" key with no explicit alias.
  if (!task || task.hasAlias || currentLabel !== task.taskKey) return currentLabel;

  const equipment = equipmentList.find((item) => item.identifier === task.instrumentName);
  if (!equipment) return currentLabel;

  return `${equipment.name}${currentLabel.slice(task.instrumentName.length)}`;
}

/**
 * Resolves a persisted run-task alias when the original task YAML is not available
 * (run tasks persist only `alias`, which was set to the raw "<identifier>.<Method>"
 * key at run creation). Treats the alias itself as the raw key: its prefix is swapped
 * for the equipment name only when it matches a known equipment identifier, so explicit
 * user aliases (whose prefix won't match an identifier) are left untouched.
 */
export function resolvePersistedTaskAlias(alias: string, equipmentList: EquipmentItem[]): string {
  return resolveTaskAlias(alias, { instrumentName: alias.split('.')[0], taskKey: alias, hasAlias: false }, equipmentList);
}
